import { defineComponent as _defineComponent } from 'vue'
import { ref, watch, provide, readonly, Ref } from "vue"
import WizardDialog from "@/components/WizardDialog/WizardDialog.vue"
import Wizard from "@/components/Wizard/Wizard.vue"
import WizardStep from "@/components/Wizard/WizardStep.vue"
import ServiceList from "@/components/client/onboarding/ServiceList.vue"
import ProjectDetails from "./ProjectDetails.vue"
import Review from "./Review.vue"
import NextSteps from "./NextSteps.vue"
import { useLazyQuery } from "@vue/apollo-composable"
import {
  AvailableMissionScopeServicesQueryQuery,
  AvailableMissionScopeServicesQuery,
  ScopeServiceFragmentFragment,
  CreateMissionRequestMutation,
  CreateMissionRequestMutationMutation,
  CreateMissionRequestMutationMutationVariables,
  MissionRequestInput,
} from "@/gql"
import { useMutation } from "@/hooks/useMutation"
import { useAccountContext } from "@/hooks/useAccountContext"
import SelectClient from "./SelectClient/SelectClient.vue"
import { useTranslation } from "@/i18n/useTranslation"
import { provideNewProjectValues } from "./context/useNewProjectContext"
import { useNotification } from "@/hooks/useNotification"
import type { ProjectDetailsForm, ClientFormValues } from "./types"


export default /*#__PURE__*/_defineComponent({
  __name: 'NewProjectWizard',
  props: {
    value: { type: Boolean },
    backText: null,
    wizardTitle: null,
    wizardSubtitle: null,
    isStaff: { type: Boolean }
  },
  emits: ["close"],
  setup(__props: any, { emit }) {

const props = __props as {
  value: boolean
  backText: string
  wizardTitle: string
  wizardSubtitle: string
  isStaff?: boolean
}





const { t } = useTranslation("Missions/NewProjectWizard")

const { mutate } = useMutation()
const { load, loading, result } = useLazyQuery<AvailableMissionScopeServicesQueryQuery>(
  AvailableMissionScopeServicesQuery
)

const showDialog = ref(false)
const projectWizard = ref<InstanceType<typeof Wizard> | null>(null)
const showCompletedPage = ref(false)
const serviceForm = ref<ProjectDetailsForm | null>(null)
const clientForm = ref<ClientFormValues | null>(null)

provideNewProjectValues({
  serviceForm,
  clientForm,
})

watch(
  () => props.value,
  (val) => {
    if (val) {
      showDialog.value = true

      load()
    }
  }
)

const gotoNext = () => {
  projectWizard.value && projectWizard.value.goToNextStep()
}

const gotoPrev = () => {
  projectWizard.value && projectWizard.value.goToPreviousStep()
}

const resetStepNavigation = () => {
  projectWizard.value && projectWizard.value.resetStepNavigation()
}

const selectedService = ref<ScopeServiceFragmentFragment | null>(null)
const updateActiveService = (service: ScopeServiceFragmentFragment) => {
  selectedService.value = service
}

const onSubmitProjectDetails = (form: Ref<ProjectDetailsForm>) => {
  serviceForm.value = form.value
  gotoNext()
}

const onClose = () => {
  selectedService.value = null
  serviceForm.value = null
  clientForm.value = null

  resetStepNavigation()

  showDialog.value = false
  emit("close")
}

const { client } = useAccountContext()

const iconMapping = {
  1: {
    icon: "pie-chart",
    backgroundColor: "#FF000814",
    iconColor: "#FF0008",
  },
  2: {
    icon: "presentation-graph",
    backgroundColor: "#FFBF2E14",
    iconColor: "#FFBF2E",
  },
  26: {
    icon: "graph-up",
    backgroundColor: "#06A86E14",
    iconColor: "#06A86E",
  },
}
provide("iconMapping", readonly(iconMapping))

const { addError } = useNotification()

const isSubmitting = ref(false)
const onSubmitProject = async () => {
  if (!serviceForm.value) {
    return addError("Something went wrong")
  }

  const payload: MissionRequestInput = {
    budget: {
      max: {
        amount: serviceForm.value.budget.amount as number,
        currency: serviceForm.value.budget.currency,
      },
      min: {
        amount: 0,
        currency: serviceForm.value.budget.currency,
      },
    },
    projectBrief: serviceForm.value.projectBrief,
    timeline: serviceForm.value.proposedEndDate,
    projectLocationType: serviceForm.value.projectLocationType,
    scopeServiceId: selectedService.value?.id,
    clientId: clientForm.value?.id || client?.id,
  }

  if (serviceForm.value.termsOfReferenceAttributes) {
    payload.termsOfReferenceAttributes = serviceForm.value.termsOfReferenceAttributes
  }

  isSubmitting.value = true

  const result = await mutate<
    CreateMissionRequestMutationMutation,
    CreateMissionRequestMutationMutationVariables
  >({
    mutation: CreateMissionRequestMutation,
    variables: {
      missionRequestInput: {
        ...payload,
      },
    },
    handlePayloadErrors: true,
  })

  if (result && !result.errors?.length && !result?.data?.createMissionRequest.error) {
    showCompletedPage.value = true
  }

  isSubmitting.value = false
}

const onCompleted = () => {
  resetStepNavigation()
  window.location.href = "/missions"
}

const serviceList = ref<InstanceType<typeof ServiceList> | null>(null)

const serviceListGotoNext = () => {
  serviceList.value?.gotoNext()
}

return { __sfc: true,emit, props, t, mutate, load, loading, result, showDialog, projectWizard, showCompletedPage, serviceForm, clientForm, gotoNext, gotoPrev, resetStepNavigation, selectedService, updateActiveService, onSubmitProjectDetails, onClose, client, iconMapping, addError, isSubmitting, onSubmitProject, onCompleted, serviceList, serviceListGotoNext, WizardDialog, Wizard, WizardStep, ServiceList, ProjectDetails, Review, NextSteps, SelectClient }
}

})